<template>
  <Hero
    data-testid="portfolio-hero"
    :class="heroClass"
    :image-small="portfolioImages.small"
    :image-medium="portfolioImages.regular"
    :image-large="portfolioImages.full"
    height="80vh"
    show-overlay
  >
    <div class="portfolio-hero__content">
      <HeadingDisplayLoader v-if="isLoading" class="portfolio-hero__loader" />
      <SectionError
        v-else-if="hasError"
        class="portfolio-hero__error"
        title="Não foi possível carregar o patrimônio neste momento"
        description="Aguarde alguns minutos e tente novamente."
        @on-retry="fetchPortfolioHero"
      />
      <div v-else>
        <div class="portfolio-hero__chips">
          <ChipContext
            v-if="portfolioHero?.showManagementIcon"
            class="portfolio-hero__chips--context"
            label="Gestão Warren"
            icon="G0010"
            context="brand"
          />
          <ChipStatus
            v-for="label in portfolioHero?.status"
            :key="label"
            data-testid="portfolio-hero-chip-status"
            class="portfolio-hero__chips--status"
            :label="label"
            status="information"
          />
        </div>

        <HeadingDisplay
          class="portfolio-hero__heading"
          :head="portfolioHero?.name"
          title=""
          :subtitle="portfolioHero?.updatedAt.formatted"
          size="medium"
        >
          <template #title-component>
            <Money>{{ portfolioHero?.totalGrossWorth.formatted }}</Money>
          </template>
        </HeadingDisplay>
        <section
          v-if="portfolioHero?.disclaimer"
          class="portfolio-hero__disclaimer"
          data-testid="portfolio-hero-disclaimer"
        >
          <BaseText
            class="portfolio-hero__disclaimer--title"
            :color="NebraskaColors.elementPrimary"
            size="p4"
          >
            {{ portfolioHero.disclaimer.title }}
          </BaseText>
          <BaseText size="p4">
            {{ portfolioHero.disclaimer.description }}
          </BaseText>
        </section>
        <section
          v-if="portfolioHero?.buttons.length"
          class="portfolio-hero__buttons"
        >
          <ButtonBoxLaunch
            v-for="(button, index) in portfolioHero?.buttons"
            :key="index"
            :icon="button.icon"
            :label="button.label"
            :disabled="
              !button.enabled || isImpersonate() || isImpersonatedByIntranet()
            "
            is-blur
            @click="actionMapper[button.action]()"
          />
        </section>
      </div>
    </div>
    <section
      v-if="!isLoading && !hasError && portfolioHero?.pendingTransaction"
      class="portfolio-hero__pending-transaction"
    >
      <div class="portfolio-hero__pending-transaction--content">
        <BaseText :color="NebraskaColors.elementAlpha">
          Saldo em trânsito
        </BaseText>
        <BaseText :color="NebraskaColors.elementOverImage">
          {{ portfolioHero?.pendingTransaction }}
        </BaseText>
      </div>
    </section>
  </Hero>
</template>

<script lang="ts" setup>
import { computed, inject, onMounted, Ref, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  ChipContext,
  ChipStatus,
  HeadingDisplay,
  ButtonBoxLaunch,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { Hero } from '@/modules/common/components/Hero';
import { HeadingDisplayLoader } from '@/modules/trade/components/HeadingDisplayLoader';
import { SectionError } from '@/modules/common/components/SectionError';
import { Money } from '@/modules/common/components/Money';

import {
  getIndexPortfolioHero,
  IPortfolioHero
} from '@/modules/wealth/services/portfolio';
import {
  isImpersonate,
  isImpersonatedByIntranet
} from '@/modules/common/services/partner';

const router = useRouter();

const portfolioId = inject('portfolioId') as Ref<string>;

const isLoading = ref(false);
const hasError = ref(false);
const portfolioHero = ref<IPortfolioHero | null>(null);

const portfolioImages = computed(() => {
  return {
    full: portfolioHero.value?.image.full,
    regular: portfolioHero.value?.image.regular,
    small: portfolioHero.value?.image.small
  };
});

const heroClass = computed(() => {
  return {
    'portfolio-hero': true,
    'portfolio-hero__inactive': portfolioHero.value?.isInactive
  };
});

const actionMapper: { [key: string]: Function } = {
  INVESTMENT: goToInvestment,
  REDEMPTION: goToWithdraw
};

function goToInvestment() {
  router.push({
    name: 'variableIncomeInvestment',
    params: { id: portfolioId.value }
  });
}

function goToWithdraw() {
  router.push({
    name: 'variableIncomeWithdraw',
    params: { id: portfolioId.value }
  });
}

async function fetchPortfolioHero() {
  isLoading.value = true;
  hasError.value = false;

  try {
    portfolioHero.value = await getIndexPortfolioHero(portfolioId.value);
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  fetchPortfolioHero();
});
</script>

<style lang="less" scoped>
.portfolio-hero {
  :deep(.hero__content) {
    padding: 0;
    margin: 0;
  }

  :deep(.max-width) {
    max-width: 100%;
  }

  &__inactive {
    :deep(.hero-content__picture--img) {
      filter: grayscale(100%);
    }
  }

  &__content {
    max-width: 832px;
    margin: 0 auto;
    padding: 0 @size-spacing-x400;
  }

  &__chips {
    margin-bottom: @size-spacing-x500;
    display: flex;
    height: 26px;
    gap: @size-spacing-x300;

    &--context {
      color: @status-brand !important;
      background: @status-brand-background !important;
    }
  }

  &__loader {
    width: 50%;
    margin-bottom: @size-spacing-x1000;
  }

  &__error {
    margin-bottom: @size-spacing-x1000;
  }

  &__heading {
    :deep(p),
    :deep(h1) {
      color: @element-over-image !important;
    }
  }

  &__disclaimer {
    padding: @size-spacing-x500;
    background-color: @background-primary;
    margin: @size-spacing-x600 0 @size-spacing-x1000;
    border-radius: @size-radius-x200;

    &--title {
      margin-bottom: @size-spacing-x150;
    }
  }

  &__buttons {
    margin: @size-spacing-x600 0 @size-spacing-x1000;
    display: flex;
    gap: @size-spacing-x300;
  }

  &__pending-transaction {
    width: 100%;
    height: 64px;
    background-color: @divider-opacity;

    &--content {
      height: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      max-width: 832px;
      margin: 0 auto;
      padding: 0 @size-spacing-x400;
    }
  }
}
</style>
